import { FC } from 'react'
import { addDisplayName, addHandlers, flowMax, addDefaultProps } from 'ad-hok'
import { addLocation } from 'shared/src/utils/addNavigate'
import { Category, trackEvent } from 'shared/src/utils/analytics'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import shareButtonImage from 'assets/share-icon.svg'
import { ImageButton } from '../ImageButton'
import { addNotificationContext } from 'components/notifications'

export const writeToClipboard = async (url: string) => {
  navigator.clipboard
    .writeText(url)
    .catch((error) => console.log('Error sharing:', error))
}

interface MediaContext {
  mediaId: number
  creatorId: number
}

interface EditContext {
  editId: number
  creatorId: number
}

interface CreatorContext {
  creatorId: number
}

type ShareContext = MediaContext | EditContext | CreatorContext

const isMediaContext = (context: ShareContext): context is MediaContext =>
  'mediaId' in context
const isEditContext = (context: ShareContext): context is EditContext =>
  'editId' in context

const getEventCategory = (context: ShareContext): Category => {
  if (isMediaContext(context)) {
    return 'media'
  } else if (isEditContext(context)) {
    return 'edit'
  } else {
    return 'profile'
  }
}

const getShareEventParams = (context: ShareContext) => {
  if (isMediaContext(context)) {
    return {
      media_id: context.mediaId,
      creator_id: context.creatorId,
    }
  } else if (isEditContext(context)) {
    return {
      edit_id: context.editId,
      creator_id: context.creatorId,
    }
  } else {
    return {
      creator_id: context.creatorId,
    }
  }
}

interface Props {
  context: ShareContext
  className?: string
  size?: number
}

export const ShareButton: FC<Props> = flowMax(
  addDisplayName('ShareButton'),
  addDefaultProps({
    size: 48,
  }),
  addLocation,
  addTranslationHelpers,
  addNotificationContext,
  addHandlers({
    onClick:
      ({ context, location: { pathname }, showNotification, t }) =>
      () => {
        let url = `${window.location.origin}${pathname}`

        if (isMediaContext(context)) {
          url = url + `#${context.mediaId}`
        }

        const sendEvent = () => {
          trackEvent(
            {
              category: getEventCategory(context),
              action: 'share',
            },
            getShareEventParams(context)
          )
        }

        writeToClipboard(url).then(() => {
          showNotification({
            text: t('shareAlert'),
            style: 'top',
          })
          sendEvent()
        })
      },
  }),
  ({ onClick, size, className }) => (
    <ImageButton
      onClick={onClick}
      image={shareButtonImage}
      width={size}
      height={size}
      className={className}
    />
  )
)
